import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { adminApi } from '../api/admin';
import { format } from 'date-fns';
import './DashboardPage.css';
import { SERIES, CHART_GRID, CHART_AXIS } from '../theme/chart';

interface DashboardData {
  totalUsers: number;
  todayUsers: number;
  weekUsers: number;
  totalPicks: number;
  activePicks: number;
  week1Count: number;
  week2Count: number;
  totalMatches: number;
  todayMatches: number;
  matchRate: number;
  dailySignups: { date: string; count: number }[];
  hourlyActivity: { hour: number; picks: number; matches: number }[];
  recentMatches: { id: string; chainFromPickId: string | null; matchedAt: string; durationDays: number | null }[];
}

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getDashboard().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">로딩 중...</div>;
  if (!data) return null;

  const kpiCards = [
    { label: '전체 유저', value: data.totalUsers, color: SERIES[1] },
    { label: '활성 픽', value: data.activePicks, color: SERIES[0] },
    { label: '오늘 매칭', value: data.todayMatches, color: SERIES[4] },
    { label: '매칭률', value: `${data.matchRate}%`, color: SERIES[3] },
  ];

  const statusCards = [
    { label: 'WEEK1 진행', value: data.week1Count, desc: '점진 공개 중', color: SERIES[1] },
    { label: 'WEEK2 대기', value: data.week2Count, desc: '결과 대기 중', color: SERIES[2] },
    { label: '오늘 가입', value: data.todayUsers, desc: '신규 유저', color: SERIES[4] },
    { label: '전체 매칭', value: data.totalMatches, desc: '누적', color: SERIES[0] },
  ];

  return (
    <div className="dashboard">
      <h2>대시보드</h2>

      <div className="kpi-grid">
        {kpiCards.map((s) => (
          <div key={s.label} className="kpi-card">
            <div className="kpi-value">{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</div>
            <div className="kpi-label">
              <span className="dot" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div className="status-grid">
        {statusCards.map((s) => (
          <div key={s.label} className="status-card">
            <div className="status-value">{s.value.toLocaleString()}</div>
            <div className="status-label">
              <span className="dot" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </div>
            <div className="status-desc">{s.desc}</div>
          </div>
        ))}
      </div>

      <div className="charts-row">
        <div className="chart-section">
          <h3>일별 가입자 (14일)</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.dailySignups}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} stroke={CHART_AXIS} tickLine={false} />
                <YAxis allowDecimals={false} fontSize={11} stroke={CHART_AXIS} tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="count" fill={SERIES[0]} radius={[4, 4, 0, 0]} name="가입" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-section">
          <h3>시간대별 활동 (오늘)</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.hourlyActivity}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                <XAxis dataKey="hour" tickFormatter={(v) => `${v}시`} fontSize={11} stroke={CHART_AXIS} tickLine={false} />
                <YAxis allowDecimals={false} fontSize={11} stroke={CHART_AXIS} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="picks" stroke={SERIES[1]} name="픽 발생" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="matches" stroke={SERIES[4]} name="매칭 성사" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {data.recentMatches.length > 0 && (
        <div className="recent-section">
          <h3>최근 매칭</h3>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>픽 ID</th>
                  <th>매칭방식</th>
                  <th>소요일</th>
                  <th>매칭일</th>
                </tr>
              </thead>
              <tbody>
                {data.recentMatches.map((m) => (
                  <tr key={m.id}>
                    <td><code>{m.id.slice(0, 8)}</code></td>
                    <td>{m.chainFromPickId ? '체인' : '상호픽'}</td>
                    <td>{m.durationDays != null ? `${m.durationDays}일` : '-'}</td>
                    <td>{format(new Date(m.matchedAt), 'MM-dd HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
